/**
 * E-goi SMS Service
 * NOW USES BACKEND FUNCTIONS - NO MORE CORS! 🎉
 */

import { base44 } from "@/api/base44Client";

export const EgoiSMSService = {
  /**
   * Send SMS via Backend Function (NO CORS!)
   * ✅ STANDARDIZED PARAMS: phoneNumber, customerId, customerName, templateUsed
   */
  async sendSMS(phoneNumber, message, sender = "GestosAgeis", customerId = null, customerName = null, templateUsed = null) {
    try {
      // 🔍 VALIDATE INPUTS
      if (!phoneNumber || !message) {
        console.error('❌ Missing required fields');
        return {
          success: false,
          error: 'Telefone e mensagem são obrigatórios'
        };
      }

      console.log('📱 [EgoiSMSService] Sending SMS via Backend Function');
      console.log('📱 [EgoiSMSService] Phone:', phoneNumber);
      console.log('📱 [EgoiSMSService] Message length:', message?.length);
      console.log('📱 [EgoiSMSService] Sender:', sender);

      // Call backend function with STANDARDIZED params
      const response = await base44.functions.invoke('sendSMS', {
        phoneNumber,  // ✅ STANDARDIZED
        message,
        sender,
        customerId,   // ✅ STANDARDIZED
        customerName, // ✅ STANDARDIZED
        templateUsed  // ✅ STANDARDIZED
      });

      console.log('📥 [EgoiSMSService] Response received:', response);
      console.log('📥 [EgoiSMSService] Response status:', response.status);
      console.log('📥 [EgoiSMSService] Response data:', response.data);

      // Check if response is successful
      if (response.status === 200 || response.status === 201) {
        const result = response.data;
        
        if (result.success) {
          console.log('✅ [EgoiSMSService] SMS sent successfully!');
          return result;
        } else {
          console.error('❌ [EgoiSMSService] SMS failed:', result.error);
          return result;
        }
      } else {
        console.error('❌ [EgoiSMSService] HTTP Error:', response.status);
        return {
          success: false,
          error: `Erro HTTP ${response.status}: ${response.data?.error || 'Erro desconhecido'}`
        };
      }
    } catch (error) {
      console.error('❌ [EgoiSMSService] Exception:', error);
      console.error('❌ [EgoiSMSService] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao enviar SMS'
      };
    }
  },

  /**
   * Send SMS to multiple recipients
   */
  async sendBulkSMS(phoneNumbers, message, sender = "GestosAgeis") {
    const results = [];

    for (const phoneNumber of phoneNumbers) {
      const result = await this.sendSMS(phoneNumber, message, sender);
      results.push({
        phoneNumber,
        ...result,
      });
      
      // Small delay between SMS to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    const successCount = results.filter(r => r.success).length;
    const failureCount = results.filter(r => !r.success).length;

    console.log(`📊 Bulk SMS Results: ${successCount} sent, ${failureCount} failed`);

    return {
      total: results.length,
      success: successCount,
      failed: failureCount,
      results,
    };
  },

  /**
   * Test E-goi connection with a test SMS
   * NOW WORKS VIA BACKEND! 🎉
   */
  async testConnection(testPhone = "351932122202") {
    const testMessage = `✅ Teste E-goi SMS - ${new Date().toLocaleTimeString('pt-PT')} - Gestos Ageis`;
    
    console.log('🧪 Testing E-goi connection via backend...');
    
    const result = await this.sendSMS(testPhone, testMessage, "GestosAgeis");
    
    if (result.success) {
      console.log('✅ E-goi test successful!');
    } else {
      console.error('❌ E-goi test failed:', result.error);
    }
    
    return result;
  },

  /**
   * Format phone number to E-goi format
   */
  formatPhoneNumber(phone) {
    const cleanPhone = phone.replace(/\D/g, '');
    
    if (cleanPhone.startsWith('351')) {
      return cleanPhone;
    }
    
    if (cleanPhone.startsWith('9') && cleanPhone.length === 9) {
      return '351' + cleanPhone;
    }
    
    return '351' + cleanPhone;
  },

  /**
   * Get predefined SMS templates
   */
  getPredefinedMessages() {
    return [
      {
        id: 'new_reservation',
        name: 'Nova Reserva',
        template: (customerName) => 
          `Ola ${customerName}! A sua reserva foi recebida. Entraremos em contacto em breve. Gestos Ageis - 21 603 5281`,
      },
      {
        id: 'approved',
        name: 'Reserva Aprovada',
        template: (customerName) => 
          `${customerName}, a sua reserva foi aprovada! Aguardamos o seu pagamento. Obrigado! Gestos Ageis - 21 603 5281`,
      },
      {
        id: 'payment_confirmed',
        name: 'Pagamento Confirmado',
        template: (customerName) => 
          `${customerName}, pagamento confirmado! A sua reserva esta confirmada. Obrigado! Gestos Ageis - 21 603 5281`,
      },
      {
        id: 'delivery_today',
        name: 'Entrega Hoje',
        template: (customerName) => 
          `${customerName}, a sua entrega esta agendada para hoje. Confirme a disponibilidade. Gestos Ageis - 21 603 5281`,
      },
      {
        id: 'pickup_reminder',
        name: 'Lembrete Devolucao',
        template: (customerName, date) => 
          `${customerName}, lembrete: devolucao agendada para ${date}. Obrigado! Gestos Ageis - 21 603 5281`,
      },
      {
        id: 'extension_approved',
        name: 'Prolongamento Aprovado',
        template: (customerName) => 
          `${customerName}, o seu pedido de prolongamento foi aprovado! Entraremos em contacto. Gestos Ageis - 21 603 5281`,
      },
      {
        id: 'thank_you',
        name: 'Agradecimento',
        template: (customerName) => 
          `${customerName}, obrigado por escolher a Gestos Ageis! Esperamos ve-lo novamente. 21 603 5281`,
      },
      {
        id: 'custom',
        name: 'Mensagem Livre',
        template: () => '',
      },
    ];
  },
};